// Removes GCS resumable-upload session ids from anything Sentry records.
//
// A resumable session url (`…/upload/storage/v1/b/<bucket>/o?uploadType=resumable&upload_id=…`) is a
// bearer write handle: whoever holds it can keep uploading into the session with no other auth
// (GCS docs). Photoshoot Studio ZIP uploads (`utils/resumableUpload`) PUT chunks to it through the
// global `fetch`, which Sentry instruments — so without this the url would ride along in fetch
// breadcrumbs, `http.client` spans (`url`, `http.url`, `http.query`, the description) and Replay's
// network entries. Only the `upload_id` value is replaced; the rest of the url stays for debugging.

import { redactLinkTokens, SECRET_FIELD_NAMES } from "utils/linkToken";

const UPLOAD_ID = /([?&]upload_id=)[^&#\s"']+/gi;

// P4-F4 (STRIPE Phase 4): the same walk also replaces one-time link tokens — a legacy page path
// (`/reset-password/<token>`), an old API path (`users/reset_password/<token>`) or a `token=` / `link_token=` parameter —
// so navigation breadcrumbs, request urls, spans and Replay never carry one (utils/linkToken).
export const scrubUploadSessionString = (value: string): string => redactLinkTokens(value.includes("upload_id=") ? value.replace(UPLOAD_ID, "$1[Filtered]") : value);

// Walks plain objects/arrays and scrubs every string. Bounded depth: breadcrumbs and spans are
// shallow; this is not meant for DOM snapshots.
export const scrubUploadSession = <T>(value: T, depth = 0): T => {
  if (typeof value === "string") return scrubUploadSessionString(value) as unknown as T;
  if (!value || typeof value !== "object" || depth > 6) return value;
  if (Array.isArray(value)) return value.map((item) => scrubUploadSession(item, depth + 1)) as unknown as T;

  const out: Record<string, unknown> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    // A structured request body (`{ token, password }`) logged as an object (P4-F4).
    out[key] = typeof item === "string" && SECRET_FIELD_NAMES.has(key) ? "[Filtered]" : scrubUploadSession(item, depth + 1);
  });

  return out as T;
};

// Replay recording events worth scrubbing: network/performance spans and breadcrumbs (rrweb custom
// events, type 5). DOM snapshots and mutations are left alone — they never carry a request url, and
// walking them on every event would be expensive.
export const scrubReplayRecordingEvent = <T extends { type?: number; data?: unknown }>(event: T): T => {
  if (event?.type !== 5) return event;
  const tag = (event.data as { tag?: string } | undefined)?.tag;
  if (tag !== "performanceSpan" && tag !== "breadcrumb") return event;

  return scrubUploadSession(event);
};

// An error or transaction event's own url context — transaction name, request url, spans, contexts, extra (HttpClient's
// route / request_path) and tags — gets the same scrub. Used by `beforeSend` AND `beforeSendTransaction` (P4-F4: an
// in-app navigation to a token url would otherwise ship it in a transaction).
type SentryEventLike = { transaction?: string; request?: unknown; spans?: unknown; contexts?: unknown; extra?: unknown; tags?: unknown };

export const scrubSentryEvent = <T extends SentryEventLike>(event: T): T => {
  const out = { ...event };
  if (out.transaction) out.transaction = scrubUploadSessionString(out.transaction);
  if (out.request) out.request = scrubUploadSession(out.request);
  if (out.spans) out.spans = scrubUploadSession(out.spans);
  if (out.contexts) out.contexts = scrubUploadSession(out.contexts);
  if (out.extra) out.extra = scrubUploadSession(out.extra);
  if (out.tags) out.tags = scrubUploadSession(out.tags);

  return out;
};
